import { usePageSEO } from "@/lib/use-page-seo";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import WCLogo from "@/components/WCLogo";
import { useLocalPrice } from "../hooks/useLocalPrice";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLocalePath } from "@/lib/use-locale-path";
import { getAttribution, getFirstAttribution } from "@/lib/attribution";

type Team = {
  id: number;
  name: string;
  code: string;
  flag: string;
  group: string;
  confederation: string;
};

export default function CheckoutPage() {
  usePageSEO("meta.checkout_title", "/checkout");
  const { bracket: localEstimate } = useLocalPrice(1.99, "USD");
  const { t } = useTranslation();
  const lp = useLocalePath();

  const params = new URLSearchParams(window.location.search);
  const userId = params.get("userId");
  const cancelled = params.get("cancelled");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user profile to personalise the page
  // After verify, we don't have a calendar token yet — we need to look up by userId
  // For now use the teams list to show what they'll get
  const { data: allTeams = [] } = useQuery<Team[]>({
    queryKey: ["/api/teams"],
  });

  const handlePay = async () => {
    if (!userId) return;
    setIsRedirecting(true);
    setError(null);
    try {
      const attribution = getAttribution();
      const firstAttribution = getFirstAttribution();
      const res = await apiRequest("POST", "/api/checkout", {
        userId: parseInt(userId),
        attribution,
        firstAttribution,
      });
      const data = await res.json();
      if (data.url) {
        (window as any).posthog?.capture('checkout_started');
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      setError(t("checkout.error"));
      setIsRedirecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-in-up">
          {cancelled ? (
            <div className="text-center mb-8">
              <p className="text-muted-foreground text-sm mb-4">{t("checkout.cancelled")}</p>
              <a href={lp("/")} className="text-primary text-sm hover:underline">{t("checkout.back_to_teams")}</a>
            </div>
          ) : null}

          <div className="rounded-2xl border border-border/60 bg-card p-8">
            {/* Trophy icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="hsl(43 95% 50%)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 21h8M12 17v4M7 4H4a2 2 0 0 0-2 2v2a4 4 0 0 0 4 4M17 4h3a2 2 0 0 1 2 2v2a4 4 0 0 1-4 4"/>
                  <path d="M7 4a5 5 0 0 0 5 5 5 5 0 0 0 5-5V2H7v2z"/>
                </svg>
              </div>
            </div>

            <h1
              className="text-3xl font-extrabold uppercase text-center mb-2"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {t("checkout.heading")}
            </h1>
            <p className="text-muted-foreground text-sm text-center mb-6">
              {t("checkout.description")}
            </p>

            {/* What you get */}
            <div className="space-y-3 mb-8">
              {[
                t("checkout.feature1"),
                t("checkout.feature2"),
                t("checkout.feature3"),
                t("checkout.feature4"),
                t("checkout.feature5"),
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="hsl(152 55% 40%)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </div>
                  <span className="text-foreground/90">{item}</span>
                </div>
              ))}
            </div>

            {/* Price callout */}
            <div className="flex items-center justify-between mb-6 px-4 py-3 rounded-lg bg-muted/40 border border-border/40">
              <span className="text-sm text-muted-foreground">{t("checkout.total")}</span>
              <span className="text-xl font-bold text-foreground" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{t("checkout.price")}</span>
              {localEstimate && <span className="text-sm text-muted-foreground ms-2">{localEstimate}</span>}
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                {error}
              </div>
            )}

            {/* Pay button with lock icon */}
            <Button
              onClick={handlePay}
              disabled={isRedirecting || !userId}
              className="w-full bg-primary text-primary-foreground font-bold text-base py-6 hover-elevate"
              data-testid="pay-btn"
            >
              {isRedirecting ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 sync-ring" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>
                  {t("checkout.redirecting")}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  {t("checkout.pay_button")}
                </span>
              )}
            </Button>

            {/* Trust signals — subtle, not over-the-top */}
            <div className="mt-5 flex flex-col items-center gap-3">
              {/* Stripe badge */}
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <span className="text-xs">{t("checkout.powered_by")}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="42" height="18" viewBox="0 0 60 25" aria-label="Stripe logo">
                  <path fill="hsl(215 20% 55%)" fillRule="evenodd" d="M59.6444 14.2813h-8.062c.1843 1.9296 1.5983 2.5476 3.2032 2.5476 1.6352 0 2.9534-.3656 4.0453-.9506v3.3179c-1.1186.7115-2.5964 1.1068-4.5645 1.1068-4.011 0-6.8218-2.5122-6.8218-7.4783 0-4.19441 2.3837-7.52509 6.3017-7.52509 3.912 0 5.9537 3.28038 5.9537 7.49819 0 .3982-.0372 1.261-.0556 1.4835Zm-5.9241-5.62407c-1.0294 0-2.1739.72812-2.1739 2.58387h4.2573c0-1.85362-1.0721-2.58387-2.0834-2.58387ZM40.9547 20.303c-1.4411 0-2.322-.6087-2.9133-1.0417l-.0088 4.6271-4.1181.8755-.0014-19.19053h3.7543l.0864 1.01784c.6035-.52914 1.6114-1.29157 3.2256-1.29162 2.8925 0 5.6162 2.6052 5.6162 7.39971 0 5.2327-2.6948 7.6037-5.6409 7.6037Zm-.959-11.35573c-.9453 0-1.5376.34559-1.9669.81586l.0245 6.11967c.3997.433.9763.7813 1.9424.7813 1.5231 0 2.5437-1.6575 2.5437-3.8745 0-2.1544-1.037-3.84233-2.5437-3.84233Zm-11.7602-3.3739h4.1341V20.0088h-4.1341V5.57337Zm0-4.694699L32.3696 0v3.35821l-4.1341.87868V.878671ZM23.9198 10.2223v9.7861h-4.1156V5.57296h3.6867l.1317 1.21751c1.0035-1.7722 3.0722-1.41321 3.6209-1.21594v3.78524c-.5242-.16908-2.2894-.42779-3.3237.86253Zm-8.5525 4.7221c0 2.4275 2.5988 1.6719 3.1263 1.4609v3.3522c-.5492.3013-1.5437.5458-2.8901.5458-2.4441 0-4.2773-1.7999-4.2773-4.2379l.0173-13.17658 4.0206-.85464.0032 3.5395h3.1278V9.0857h-3.1278v5.8588-.0001Zm-4.9069.7026c0 2.9645-2.31051 4.6562-5.73464 4.6562-1.41958 0-2.92289-.2761-4.453935-.9347v-3.9319c1.382085.7516 3.093705 1.315 4.457755 1.315.91864 0 1.53106-.2459 1.53106-1.0069C6.26064 13.7786 0 14.5192 0 9.95995 0 7.04457 2.27622 5.2998 5.61655 5.2998c1.36404 0 2.72806.20934 4.09208.75351V9.9317c-1.25265-.67618-2.84332-1.05979-4.09588-1.05979-.86296 0-1.44753.24965-1.44753.8924.0001 1.85329 6.29518.97249 6.29518 5.88279v-.0001Z" clipRule="evenodd"/>
                </svg>
              </div>

              {/* Security note */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>{t("checkout.security")}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
